<template>
  <div
    class="fixed w-full h-full inset-0 z-modal flex items-center justify-center bg-backdrop"
    @keydown.esc.stop="close"
    @mousedown.self="close"
  >
    <div
      class="flex flex-col w-full max-w-lg min-h-48 p-5 bg-white rounded-sm shadow-md"
    >
      <h1>{{ title }}</h1>
      <div v-if="showUploader">
        <label
          for="fileInput"
          class="inline-block mb-4 p-2 border cursor-pointer text-s text-gray-700 hover:bg-gray-300"
          >Upload image</label
        >
        <input
          id="fileInput"
          type="file"
          accept="image/*"
          class="hidden"
          @change="onFileChange"
        />
      </div>
      <form class="flex flex-col mb-4">
        <div v-if="isLink" class="LinkBlock">
          <span class="LabelText">Link text:</span>
          <span class="flex items-center pl-2 text-gray-500 italic">{{
            text
          }}</span>
        </div>
        <div class="LinkBlock">
          <label for="linkInput" class="LabelText">{{ inputLabel }}</label>
          <input
            id="linkInput"
            ref="linkInput"
            v-model="data"
            class="Input"
            type="text"
            placeholder="https://"
            @keydown.enter.prevent="submit"
            @keydown.esc="close"
          />
          <lm-icon-button
            v-if="isLink"
            class="ml-2"
            icon="openInNew"
            tooltip="Test link"
            :disabled="!isValid"
            svg
            @click.prevent="openLink"
          />
        </div>
        <div
          v-if="showInfo"
          class="flex my-4 p-2 bg-gray-100 border border-dashed"
        >
          <lm-icon icon="info-outline" class="mr-2" />
          <p class="mb0-important" v-html="infoMessage" />
        </div>
      </form>
      <div class="mt-auto flex">
        <lm-modal-button
          v-if="isLink"
          title="Remove Link"
          :disabled="!url"
          @click="$emit('clear')"
        />
        <lm-modal-button title="Cancel" class="ml-auto mr-2" @click="close" />
        <lm-modal-button
          title="Insert"
          :disabled="!isValid"
          type="primary"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>

<script>
import LmIconButton from '@/components/ui/LmIconButton'
import {Resizer} from 'yet-another-resizer'
import LmModalButton from '@/components/ui/button/LmModalButton'
import LmIcon from '@/components/ui/LmIcon'
import {Formats} from '@/lib/definitions'
import {isValidMediaUrl, isBase64Image, urlRegExp} from './mediaHelpers'

export default {
  name: 'LinkBox',
  components: {
    LmIcon,
    LmIconButton,
    LmModalButton
  },
  props: {
    text: {
      type: String,
      default: null
    },
    url: {
      type: String,
      default: null
    },
    extension: {
      validator: function(value) {
        return (
          [Formats.IFRAME, Formats.IMAGE, Formats.LINK].indexOf(value) !== -1
        )
      },
      required: true
    }
  },
  data() {
    return {
      data: ''
    }
  },
  computed: {
    infoMessage() {
      if (this.extension === Formats.IMAGE)
        return 'You can also drag and drop images from your computer or your browser directly into the Editor.'
      else if (this.extension === Formats.IFRAME)
        return "Supported platforms are YouTube, Vimeo and SlideShare.<br/>Copy and paste the share link or, in the case of SlideShare, the 'Embed' code."

      return ''
    },
    title() {
      let t = 'Insert Link'
      if (this.extension === Formats.IFRAME) t = 'Insert Media from URL'
      else if (this.extension === Formats.IMAGE) t = 'Insert Image URL'
      return t
    },
    inputLabel() {
      let t = 'Link URL:'
      if (this.extension === Formats.IFRAME) t = 'Media URL:'
      else if (this.extension === Formats.IMAGE) t = 'or add URL:'
      return t
    },
    isLink() {
      return this.extension === Formats.LINK
    },
    showInfo() {
      return (
        this.extension === Formats.IMAGE || this.extension === Formats.IFRAME
      )
    },
    showUploader() {
      return this.extension === Formats.IMAGE
    },
    isValid() {
      if (this.extension === Formats.IFRAME) {
        return isValidMediaUrl(this.data)
      }
      if (this.extension === Formats.IMAGE) {
        if (isBase64Image(this.data)) return true
      }
      return this.data && this.data.match(urlRegExp)
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.linkInput.focus()
    })
    if (this.url) this.data = this.url
  },
  methods: {
    submit() {
      if (this.isValid) this.$emit('submit', this.data)
    },
    close() {
      this.$emit('close')
    },
    onFileChange(e) {
      let files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      let file = files[0]
      if (file.type.indexOf('image') !== -1) {
        this.createImage(files[0])
      }
    },
    createImage(file) {
      let reader = new FileReader()
      reader.onload = e => {
        let tmpImg = new Image()
        this.resizer = new Resizer({maxWidth: 582})
        tmpImg.onload = () => {
          if (tmpImg.width && tmpImg.height) {
            this.resizer.scaleImage(tmpImg).then(imageData => {
              this.data = imageData
              this.submit()
            })
          }
        }
        tmpImg.src = e.target.result
      }
      reader.readAsDataURL(file)
    },
    openLink() {
      window.open(this.data, '_blank')
    }
  }
}
</script>

<style scoped>
.LinkBlock {
  @apply flex items-center mb-3;
}
.LabelText {
  @apply w-20 mr-2 text-gray-700;
}
.Input {
  @apply h-7 flex-grow px-2 border text-gray-900;
}
</style>
